// packages/web/src/components/ui/Select.tsx — misma API.
import { useId, type ReactNode } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

interface Props<T> {
  label: string;
  value: T | null;
  options: readonly T[];
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  onChange: (next: T | null) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  disabledPlaceholder?: string;
  emptyMessage?: string;
  hint?: ReactNode;
}

export function Select<T>({
  label, value, options, getKey, getLabel, onChange, disabled = false, loading = false,
  placeholder = 'Elige una opción…', disabledPlaceholder, emptyMessage, hint,
}: Props<T>) {
  const id = useId();
  const currentPlaceholder = disabled && disabledPlaceholder
    ? disabledPlaceholder
    : loading ? 'Cargando…'
    : options.length === 0 && emptyMessage ? emptyMessage
    : placeholder;
  // Hint visible cuando está bloqueado: explica el orden de la cascada.
  const helper = disabled && disabledPlaceholder ? disabledPlaceholder : hint;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-800">{label}</label>
      <div className="relative mt-1.5">
        <select
          id={id}
          aria-describedby={helper ? `${id}-hint` : undefined}
          className={
            'w-full appearance-none rounded-lg border border-slate-300 bg-white pl-3 pr-9 py-2 text-sm text-slate-900 ' +
            'focus:outline-none focus:ring-2 focus:ring-marzano-500 focus:border-marzano-500 ' +
            'disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed truncate'
          }
          value={value ? getKey(value) : ''}
          disabled={disabled || loading}
          onChange={(e) => onChange(options.find((o) => getKey(o) === e.target.value) ?? null)}
        >
          <option value="">{currentPlaceholder}</option>
          {options.map((o) => <option key={getKey(o)} value={getKey(o)}>{getLabel(o)}</option>)}
        </select>
        {loading
          ? <Loader2 aria-hidden className="w-4 h-4 animate-spin text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          : <ChevronDown aria-hidden className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />}
      </div>
      {helper && <p id={`${id}-hint`} className="text-[13px] text-slate-600 mt-1.5">{helper}</p>}
    </div>
  );
}
